import ToastContext from 'src/store/toast-context';
import { useState, useReducer, useCallback, useContext, useEffect } from 'react';
import authReducer from 'src/store/reducers/authReducer';
import retrieveRemainingSessionTime from 'src/util/retrieveRemainingSessionTime';
import { rawAPI } from 'src/services/api';

const useAuthentication = ({ logoutEndpoint, accessTokenEndpoint }) => {
  const { handleError, success, error } = useContext(ToastContext);
  const [initialized, setInitialized] = useState(false);
  const [loginState, dispatch] = useReducer(authReducer, {
    token: '',
    account: null,
    remainingTime: retrieveRemainingSessionTime(),
    logoutNotified: false,
    backdoor: Boolean(localStorage.getItem('backdoor')),
  });

  const logoutHandler = useCallback(
    ({ enableNotification = true, sessionExpired = false } = {}) => {
      const logout = async () => {
        try {
          //This endpoint is not implemented yet
          await rawAPI.post(logoutEndpoint);
          // Clear the session storage
          sessionStorage.clear();
          dispatch({ type: 'logout' });
          if (sessionExpired === true) {
            error('Session expired. Please log in.', {
              onOpen: () => {
                dispatch({ type: 'clear-logout-notified' });
              },
            });
          } else {
            if (enableNotification) {
              success('Logged out!', {
                onOpen: () => {
                  dispatch({ type: 'clear-logout-notified' });
                },
              });
            }
          }
        } catch (err) {
          handleError(err);
        }
      };

      logout();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleError, success, error]
  );

  const loginHandler = useCallback((token, exp) => {
    dispatch({ type: 'login', payload: { token, exp } });
  }, []);

  const clearAccessToken = useCallback(() => {
    dispatch({ type: 'clear-token' });
  }, []);

  const loginBackdoorHandler = useCallback((token, exp) => {
    dispatch({ type: 'loginBackdoor', payload: { token, exp } });
  }, []);

  const updateAccountHandler = (account) => {
    dispatch({ type: 'updateAccount', payload: account });
  };

  const refreshSession = (exp) => {
    dispatch({ type: 'refreshSession', payload: exp });
  };

  // Access-token initialization
  useEffect(() => {
    const initSession = async () => {
      const response = await rawAPI.post(accessTokenEndpoint, undefined, {
        validateStatus: null,
      });

      if (response.status === 200) {
        loginHandler(response.headers['x-access-token'], response.data.exp);
      }

      setInitialized(true);
    };

    if (!initialized) {
      initSession();
    }
    // Only want this effect to trigger on initial render, leaving deps array blank.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    initialized,
    loginState,
    logoutHandler,
    loginHandler,
    clearAccessToken,
    loginBackdoorHandler,
    updateAccountHandler,
    refreshSession,
  };
};

export default useAuthentication;
